// composables/useRecorder.ts
import { reactive, onUnmounted, ref } from 'vue';

declare global {
    interface Window {
        webkitAudioContext: typeof AudioContext;
    }
}

interface RecorderState {
    // canvasEl: HTMLCanvasElement | null;
    mediaStream: MediaStream | null;
    mediaRecorder: MediaRecorder | null;
    audioContext: AudioContext | null;
    analyser: AnalyserNode | null;
    audioChunks: Blob[];
    prevData: number[];
    animationId: number | null;
    isRecording: boolean;
}

export default function useRecorder() {
    const state = reactive<RecorderState>({
        // canvasEl: null,
        mediaStream: null,
        mediaRecorder: null,
        audioContext: null,
        analyser: null,
        audioChunks: [],
        prevData: [],
        animationId: null,
        isRecording: false,
    });
    const canvasEl = ref<HTMLCanvasElement | null>(null);

    const initAudioAnalyser = () => {
        console.log('初始化音频分析器...', canvasEl.value, state.mediaStream);
        if (!state.mediaStream || !canvasEl.value) return;
        console.log('初始化音频分析器...');
        state.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        state.analyser = state.audioContext.createAnalyser();
        state.analyser.fftSize = 256;

        const source = state.audioContext.createMediaStreamSource(state.mediaStream);
        source.connect(state.analyser);
        state.prevData = new Array(state.analyser.frequencyBinCount).fill(0);
    };

    const drawSpectrum = () => {
        if (!canvasEl.value || !state.analyser) return;
        console.log('开始绘制频谱图...');
        const ctx = canvasEl.value.getContext('2d')!;
        const dataArray = new Uint8Array(state.analyser.frequencyBinCount);
        const { width } = canvasEl.value;
        const { height } = canvasEl.value;

        const drawFrame = () => {
            state.analyser!.getByteFrequencyData(dataArray);
            state.prevData = state.prevData.map((val, i) => val * 0.3 + dataArray[i] * 0.7);

            ctx.fillStyle = 'rgba(256,256,256,1)';
            ctx.fillRect(0, 0, width, height);

            const centerX = width / 2;
            const maxBars = Math.floor(state.prevData.length / 2);

            for (let i = 0; i < maxBars; i += 1) {
                const barHeight = state.prevData[i] * 0.2;
                const hue = 90 + i * 0.3;
                const saturation = 80 + i * 0.5;
                const lightness = 40 + barHeight * 0.2;

                ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                ctx.fillRect(centerX + i * 6, height - barHeight, 4, barHeight);
                ctx.fillRect(centerX - (i + 1) * 6, height - barHeight, 4, barHeight);
            }
            console.log('drawing frame...');
            state.animationId = requestAnimationFrame(drawFrame);
        };

        drawFrame();
    };

    const startRecording = async () => {
        try {
            console.log('请求录音权限...');
            state.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log('获取录音权限成功');
            initAudioAnalyser();

            state.mediaRecorder = new MediaRecorder(state.mediaStream);
            state.mediaRecorder.ondataavailable = e => {
                state.audioChunks.push(e.data);
            };

            state.mediaRecorder.start();
            state.isRecording = true;
            drawSpectrum();
        } catch (error) {
            console.error('录音权限被拒绝:', error);
            state.isRecording = false;
        }
    };

    const cleanupResources = () => {
        if (state.animationId) {
            cancelAnimationFrame(state.animationId);
        }
        state.analyser?.disconnect();
        state.audioContext?.close();
    };

    const stopRecording = (): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            if (!state.mediaRecorder || !state.isRecording) {
                reject(new Error('录音尚未开始'));
                return;
            }

            state.mediaRecorder.stop();

            // 处理录音数据
            state.mediaRecorder.ondataavailable = e => {
                state.audioChunks.push(e.data);
            };

            state.mediaRecorder.onstop = () => {
                const blob = new Blob(state.audioChunks, { type: 'audio/webm' });
                state.audioChunks = [];
                state.isRecording = false;
                resolve(blob);
            };

            state.mediaRecorder.onerror = event => {
                reject(event);
            };

            state.mediaStream?.getTracks().forEach(track => track.stop());
            cleanupResources();
        });
    };

    onUnmounted(() => {
        cleanupResources();
    });

    return {
        canvasEl,
        startRecording,
        stopRecording,
    };
}
